import { payrollRunApiBaseUrl, reportsApiBaseUrl } from '../../shared/env';
import { createJsonClient } from '../../shared/httpClient';
import type { ReportsGateway } from './ReportsGateway';
import type {
  CashRequirementResponse, EmployeePaySummaryResponse, EmployeePayslipListQuery, EmployeePayslipListResponse, EmployeePayslipResponse,
  PayrollRegisterResponse, PayrollRunSummary,
} from './types';

export class HttpReportsGateway implements ReportsGateway {
  private readonly request = createJsonClient(reportsApiBaseUrl);

  // Reads Module 7 directly — see ReportsGateway's own doc comment for why. Filtered client-side to
  // Approved, the same convention AccountingGateway's own `listApprovedPayrollRuns` already uses.
  private readonly payrollRunRequest = createJsonClient(payrollRunApiBaseUrl);

  async listApprovedPayrollRuns(signal?: AbortSignal): Promise<PayrollRunSummary[]> {
    const runs = await this.payrollRunRequest<PayrollRunSummary[]>('/api/payroll-runs', undefined, signal);
    return runs.filter((r) => r.status === 'Approved');
  }

  getPayrollRegister(payrollRunId: string, signal?: AbortSignal): Promise<PayrollRegisterResponse> {
    return this.request(`/api/reports/payroll-runs/${payrollRunId}/register`, undefined, signal);
  }

  getCashRequirement(payrollRunId: string, signal?: AbortSignal): Promise<CashRequirementResponse> {
    return this.request(`/api/reports/payroll-runs/${payrollRunId}/cash-requirement`, undefined, signal);
  }

  getEmployeePaySummary(employeeId: string, from: string, to: string, signal?: AbortSignal): Promise<EmployeePaySummaryResponse> {
    return this.request(`/api/reports/employees/${employeeId}/pay-summary?from=${from}&to=${to}`, undefined, signal);
  }

  getEmployeePayslip(payrollRunId: string, employeeId: string, signal?: AbortSignal): Promise<EmployeePayslipResponse> {
    return this.request(`/api/reports/payroll-runs/${payrollRunId}/employees/${employeeId}/payslip`, undefined, signal);
  }

  listEmployeePayslips(query: EmployeePayslipListQuery, signal?: AbortSignal): Promise<EmployeePayslipListResponse> {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== '') params.set(key, String(value));
    }
    return this.request(`/api/reports/employee-payslips?${params.toString()}`, undefined, signal);
  }
}
